import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ConstructionIcon from '@mui/icons-material/Construction';

import SHAOverview from './SHAOverview';
import SHAPlanningProgress from './SHAPlanningProgress';
import SHAReport from './SHAReport';

const DARK_GREEN = '#1D4434';
const TAB_NAMES = ['overview', 'planning-progress', 'report'];

/**
 * Service Health Assessment (SHA) — Platform & Ops landing.
 *
 * Tabs:
 *   • Overview            — intent / planned sections (work-in-progress scaffold)
 *   • Planning & Progress — ADO Epic #895612 feature progress dashboard
 *
 * Tab state is URL-driven via ?tab=planning-progress so the view can be deep-linked
 * from the sidebar / bookmarks, matching the MigrationsView pattern.
 */
const SHAView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'overview';
  const initialIndex = TAB_NAMES.includes(tabParam) ? TAB_NAMES.indexOf(tabParam) : 0;
  const [activeTab, setActiveTab] = useState(initialIndex);

  useEffect(() => {
    const idx = TAB_NAMES.includes(tabParam) ? TAB_NAMES.indexOf(tabParam) : 0;
    setActiveTab(idx);
  }, [tabParam]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
    const next = new URLSearchParams(searchParams);
    next.set('tab', TAB_NAMES[newValue]);
    setSearchParams(next);
  };

  return (
    <Box>
      {/* Compact single-row header — keeps the top of the page tight so
          the tab content (which is the actual work surface) is above the fold. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.25,
          mb: 1.5
        }}
      >
        <MonitorHeartIcon sx={{ color: DARK_GREEN, fontSize: 22 }} />
        <Typography variant="h6" sx={{ color: DARK_GREEN, fontWeight: 600, lineHeight: 1.2 }}>
          Service Health Assessment
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          · Platform &amp; Ops
        </Typography>
        <Chip
          icon={<ConstructionIcon sx={{ fontSize: 14 }} />}
          label="WIP"
          size="small"
          sx={{
            height: 20,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            fontWeight: 500,
            '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' }
          }}
        />
      </Box>

      <Paper sx={{ mb: 3 }} variant="outlined">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            minHeight: 40,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              minHeight: 40,
              py: 1
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Planning & Progress" />
          <Tab label="Report" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {activeTab === 0 && <SHAOverview />}
          {activeTab === 1 && <SHAPlanningProgress />}
          {activeTab === 2 && <SHAReport />}
        </Box>
      </Paper>
    </Box>
  );
};

export default SHAView;
